import { useEffect, useRef } from "react";
import { useAnalysisStore } from "@/stores/useAnalysisStore";
import type { AnalysisFeature } from "@/types";
import {
  identifiedPointColor,
  MEASURED_ONLY_COLOR,
} from "@/utils/featureMapping";
import {
  enlargedDotRadius,
  paintScanHighlight,
  plotRectWithin,
  projectScanMarks,
  renderedPointRadius,
} from "@/utils/mzrtOverlay";
import type { MZRTChartDataPoint } from "./MZRTChartAdapter";

/**
 * 탑 플롯(m/z vs RT)의 **선택 표시**.
 *
 * 2026-09-03 (운영자 지적): "탑 플롯에서 선택된 표시가 아래 테이블에 선택되면
 * 같이 표시돼야 하지 않나."
 *
 * 확인해 보니 **어느 경로로 골라도 탑 플롯에는 표시가 생기지 않았다.** 표에서
 * 고르든, TIC 에서 고르든, 탑 플롯의 점을 직접 누르든 산점도 그림은 픽셀 단위로
 * 동일했다(선택 전후 마크업 해시·길이 동일). 바뀌는 것은 그래프 **아래**의
 * 초록 글상자뿐이었다. 고장이 아니라 **처음부터 만들지 않은 것**이다 —
 * 저장소 전 이력(브랜치 21개)에 점 단위 선택 강조가 커밋된 적이 없다.
 *
 * ## 왜 별도 컴포넌트인가
 *
 * 선택 상태를 `MZRTChartRecharts` 의 prop 으로 넣으면, 선택이 바뀔 때마다
 * 산점도 전체가 다시 만들어진다(실측 1회 307ms · 점 485개 · SVG 요소 1,117개).
 * 클릭할 때마다 화면이 한 번씩 멈추게 된다.
 *
 * 그래서 **store 를 직접 구독하는 작은 컴포넌트**가 차트 위 덧그림 층에만
 * 그린다. 선택이 바뀌면 이 컴포넌트만 다시 렌더되고 차트는 그대로 있다.
 * 같은 화면의 `MZRTPlotLegendStrip` 이 이미 쓰고 있는 방식이다
 * ("LegendStrip은 store slice 직접 구독 — ScatterChart 자체는 리렌더되지 않음").
 *
 * ## 표기
 *
 * 호버 강조와 **같은 표기**를 쓴다 — 그 스캔의 점들에 링을 두르고, 크기는
 * 그려진 점과 같다. 색만 다르다(선택=파랑, 표의 선택 행과 같은 색 / 호버=검정).
 * 그래서 "지금 고른 것"과 "지금 누르면 잡힐 것"이 한 화면에 있어도 구분된다.
 *
 * 2026-09-03 (운영자 검토 후 정정 2건):
 *   · 세로 실선을 **긋지 않는다** — "선택된 상태에서 선이 그어지는데 이건
 *     필요 없다. 호버돼서 활성화만 되면 된다."
 *   · 링 크기를 **그려진 점에서 읽는다** — recharts 는 `<Scatter r>` 를 크기에
 *     쓰지 않아 실제 점·링이 4.51px 인데, 덧그림에서 12px 로 그렸더니 표시가
 *     크게 부풀어 어색해졌다("확대 표시가 너무 어색해").
 */

/**
 * 선택 표식의 테두리 굵기 — 색은 호버와 같고 **굵기만** 다르다.
 *
 * 2026-09-06: 종전에는 파랑 `#2563eb` 로 그어 "화면 전체에서 파랑 = 선택"
 * 규칙을 따랐다. 동정 점이 violet 램프가 된 뒤로 그 파랑이 채움 위에서
 * OKLab 11.2~12.2(하한 15)·색각 이상 0.66~1.49 로 묻혀 쓸 수 없다.
 * 색 대신 굵기로 가른다 — 호버 1px / 선택 2px, 둘 다 점 가장자리에 딱 붙는다.
 * 선택이 지금 무엇인지는 표의 선택 행·사이드바·범례 줄이 이름으로 말한다.
 */
/** 점 크기를 못 읽었을 때의 대비값 — recharts 기본 심볼(면적 64)의 반지름. */
const DOT_RADIUS_FALLBACK = 4.51;
const SELECTION_STROKE_WIDTH = 2;

/** RT 비교 키 — 부동소수 오차로 같은 스캔이 갈리지 않게 3자리 고정. */
const rtKey = (rt: number) => rt.toFixed(3);

interface MZRTSelectionOverlayProps {
  data: MZRTChartDataPoint[];
  /**
   * 후보 행 목록 — **선택한 후보의 스캔(RT)을 여기서 찾는다.**
   *
   * 점만으로는 찾을 수 없다: `mzrt-data` 는 스캔을 **1순위 후보의 F-번호로만**
   * 라벨하므로 같은 스캔의 형제 후보는 자기 점이 없다(실측 분석 326: 후보
   * 31건 중 11건 — F3·F6·F10·F11·F12·F16·F17·F18·F22·F27·F31). 종전에는
   * feature_id 로 점을 찾아서, 이 11건을 표에서 고르면 탑 플롯에 아무 표시도
   * 나타나지 않았다(운영자 지적 2026-09-03).
   */
  features?: AnalysisFeature[];
  /** 점 색 재현에 필요하다 — 차트가 쓰는 것과 같은 값이어야 한다. */
  featureMaxIntensities: Record<string, number>;
  xAxisType: "rt" | "ri";
  /** 차트가 실제로 쓰는 축 도메인 — 표식이 점 위에 얹히려면 같아야 한다. */
  xDomain: [number, number] | null;
  yDomain: [number, number] | null;
}

const MZRTSelectionOverlay = ({
  data,
  features,
  featureMaxIntensities,
  xAxisType,
  xDomain,
  yDomain,
}: MZRTSelectionOverlayProps) => {
  const svgRef = useRef<SVGSVGElement>(null);
  const selectedFeature = useAnalysisStore((s) => s.selectedFeatureId);
  /**
   * 후보 없이 **스캔만** 고른 경우 — 측정만 된(회색) 점을 누르면 store 가
   * `selectedFeatureId: null, selectedRT: rt` 로 만든다(운영자 결정 7-a).
   *
   * 종전에는 이 경로에서 탑 플롯에 아무 표시도 생기지 않았다. 질량스펙트럼만
   * 바뀌므로 **어디를 눌렀는지 화면에 흔적이 없었고**, 스펙트럼을 보다가
   * 원래 자리로 돌아갈 길도 없었다(운영자 지적 2026-09-06).
   */
  const selectedScanRT = useAnalysisStore((s) => s.selectedRT);

  useEffect(() => {
    const svg = svgRef.current;
    const root = svg?.parentElement;
    if (!svg || !root) return;

    const clear = () => paintScanHighlight(svg, [], { radius: 0 });

    const draw = () => {
      if (!xDomain || !yDomain) {
        clear();
        return;
      }

      // 선택한 **스캔**의 RT 를 찾는다.
      //   · 후보를 골랐으면 후보 목록에서, 없으면 점에서 — 형제 후보는 자기
      //     점이 없으므로 후보 목록이 먼저다.
      //   · 후보 없이 스캔만 골랐으면(회색 점 클릭) store 의 selectedRT 다.
      const selectedRt = selectedFeature
        ? (features?.find((f) => f.feature_id === selectedFeature)?.rt ??
          data.find((point) => point.feature_id === selectedFeature)?.rt)
        : selectedScanRT;
      const plot = plotRectWithin(root);
      if (selectedRt == null || !plot) {
        clear();
        return;
      }

      // 그 스캔의 점을 강조한다(호버와 같은 단위). 비교는 **RT** 로 한다 —
      // 후보 목록에는 RI 가 없고, 점은 두 값을 다 갖고 있다.
      //
      // 후보가 기록된 스캔이면 동정 점만, 아니면 측정된 점을 그대로 쓴다.
      // 표기는 **한 가지**다(커진 점 + 딱 붙는 테두리) — 부류는 이미 점 색이
      // 말하므로 새 기호를 만들지 않는다.
      const key = rtKey(selectedRt);
      const radius = renderedPointRadius(root);
      const scanPoints = data.filter((point) => rtKey(point.rt) === key);
      const identified = scanPoints.filter((point) => point.feature_id);
      const marked = identified.length > 0 ? identified : scanPoints;
      if (marked.length === 0 || !radius) {
        clear();
        return;
      }

      const marks = projectScanMarks(
        marked.map((point) => ({
          x: xAxisType === "rt" ? point.rt : (point.ri ?? point.rt),
          mz: point.mz,
          // 원래 점과 **같은 색** — 커진 점이 다른 색이면 다른 점처럼 보인다.
          color: point.feature_id
            ? identifiedPointColor(
                point.intensity,
                featureMaxIntensities[point.feature_id] ?? 0,
              )
            : MEASURED_ONLY_COLOR,
        })),
        plot,
        xDomain,
        yDomain,
      );

      // 선택은 호버와 **같은 "커진 점"** 이되, 테두리로 구분한다 — 호버는
      // 스쳐 지나가고 선택은 남으므로 한 화면에 함께 있어도 갈려야 한다.
      paintScanHighlight(svg, marks, {
        radius: enlargedDotRadius(radius ?? DOT_RADIUS_FALLBACK),
        outline: { width: SELECTION_STROKE_WIDTH },
      });
    };

    // 진입 직후에는 차트가 아직 자리를 잡지 않아 좌표를 읽을 수 없다
    // (ResponsiveContainer 가 크기를 잰 뒤에 격자가 생긴다). 읽힐 때까지
    // 몇 프레임 더 본다 — 못 읽으면 그리지 않으므로 표시가 없는 채로 남는다.
    let frame = 0;
    let attempts = 0;
    const drawWhenLaidOut = () => {
      draw();
      if (!plotRectWithin(root) && attempts < 60) {
        attempts += 1;
        frame = requestAnimationFrame(drawWhenLaidOut);
      }
    };
    drawWhenLaidOut();

    // 창 크기가 바뀌면 축이 다시 그려지므로 좌표도 다시 읽어야 한다.
    const observer = new ResizeObserver(draw);
    observer.observe(root);
    return () => {
      if (frame) cancelAnimationFrame(frame);
      observer.disconnect();
    };
  }, [
    selectedFeature,
    selectedScanRT,
    data,
    features,
    featureMaxIntensities,
    xAxisType,
    xDomain,
    yDomain,
  ]);

  return (
    <svg
      ref={svgRef}
      data-testid="mzrt-selection-overlay"
      aria-hidden="true"
      className="pointer-events-none absolute inset-0 h-full w-full"
    >
      <title>선택한 관찰 표시</title>
    </svg>
  );
};

export default MZRTSelectionOverlay;
